import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { camelCase } from 'lodash'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { Controller, RegisterOptions, useForm } from 'react-hook-form'
import { useNavigation, useRoute } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Divider } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ScrollableAvoidKeyboard, useActionBottomSheet } from '@components'
import { Button, Input, Text } from '@components/base'
import { AppRouteProp } from '@navigation/types'
import { NavigationContainer } from '@screens/Common/containers'
import { IngredientPortionForm } from '@screens/Ingredients'
import { toUnderscore, toWord } from '@screens/Ingredients/transforms'
import { normalizeNutrisenseItem } from '@screens/Ingredients/utils'
import { customIngredientsMacrosSelector, nutritionUnitsSelector } from '@selectors/app'
import { ingredientsSelector } from '@selectors/ingredients'
import { useGoBack } from '@utils/navigation'
import { useSnack } from '@utils/navigatorContext'
import { validateFloatNumberInput } from '@utils/validators'
import { Ingredient } from '@src/types'

const REQUIRED_FIELDS = ['description', 'serving', 'calories']
const SERVING_OPTIONS = ['Gram', 'Ounce', 'Cup', 'Other']

const getValidationRules = (fieldName: string) => {
  const rules: RegisterOptions = {}

  if (REQUIRED_FIELDS.includes(fieldName)) {
    rules.required = `${toWord(fieldName)} is required`
  }

  return rules
}

export const CustomIngredient = () => {
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const navigation = useNavigation()
  const goBack = useGoBack()
  const showSnack = useSnack()
  const showActionBottomSheet = useActionBottomSheet()
  const {
    control,
    formState: { isSubmitting, errors },
    getValues,
    handleSubmit,
    setValue,
  } = useForm()
  const { params } = useRoute<AppRouteProp<'CustomIngredient'>>()
  const { ingredientId, name } = params || {}

  const macros = useSelector(customIngredientsMacrosSelector)
  const allIngredients = useSelector(ingredientsSelector)
  const units = useSelector(nutritionUnitsSelector)

  const editedIngredient = allIngredients.find((ingredient) => ingredient.id === ingredientId)

  const [serving, setServing] = useState(editedIngredient?.servingUnits || SERVING_OPTIONS[0])
  const [quantity, setQuantity] = useState(`${editedIngredient?.servingAmount || 1}`)

  const insets = useSafeAreaInsets()

  const isNewIngredient = !ingredientId

  useEffect(() => {
    if (editedIngredient) {
      const { nutrition } = normalizeNutrisenseItem(editedIngredient)

      setValue('description', editedIngredient.description)

      Object.entries(nutrition).forEach(([key, value]) => {
        setValue(camelCase(key), `${value}`)
      })
    }
  }, [editedIngredient, setValue])

  useEffect(() => {
    if (name) {
      setValue('description', name)
    }
  }, [name, setValue])

  const openCancelModal = useCallback(() => {
    const hasChanges = Object.values(getValues()).some((value) => value?.trim().length > 0)

    if (!hasChanges) {
      return goBack()
    }

    showActionBottomSheet({
      body: (
        <Text bold type="large">
          Do you really want to cancel the new ingredient?
        </Text>
      ),
      primaryButton: {
        text: 'Yes, Cancel It',
        onPress: goBack,
      },
    })
  }, [getValues, goBack, showActionBottomSheet])

  const rightAccessories = useMemo(() => {
    const saveIngredient = (form: Record<string, string>) => {
      const nutrition = macros.reduce((acc, { name }) => {
        return form[name] ? { ...acc, [toUnderscore(name)]: Number(form[name]) } : acc
      }, {} as Record<string, number>)

      const payload = {
        ingredientId: editedIngredient?.id,
        description: form.description,
        servingAmount: Number(quantity) || 1,
        servingUnits: serving === 'Other' ? form.serving : serving,
        calories: nutrition.calories,
        nutrition,
        id: editedIngredient?.id,
        createdAt: editedIngredient?.createdAt,
        __typename: 'Ingredient',
      }

      dispatch({
        type: 'ingredients/submit',
        payload,
        success: (result: Ingredient) => {
          navigation.navigate('IngredientSearch', { createdIngredientId: result.id }, { pop: true })
        },
        failure: () => {
          showSnack('Ingredient could not be saved. Please try again later.', null, 'error')
        },
      })
    }
    return [
      <Button
        type="transparent"
        size="small"
        key="save-button"
        accessibilityLabel="Save"
        disabled={isSubmitting}
        onPress={handleSubmit(saveIngredient)}
      >
        Save
      </Button>,
    ]
  }, [
    dispatch,
    editedIngredient?.createdAt,
    editedIngredient?.id,
    handleSubmit,
    isSubmitting,
    macros,
    navigation,
    quantity,
    serving,
    showSnack,
  ])

  return (
    <NavigationContainer
      showLoadingIndicator
      goBack={isNewIngredient ? openCancelModal : goBack}
      title={isNewIngredient ? 'New Ingredient' : 'Edit Ingredient'}
      rightAccessories={rightAccessories}
    >
      <ScrollableAvoidKeyboard
        keyboardShouldPersistTaps="handled"
        enableResetScrollToCoords={false}
        extraHeight={120}
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
      >
        <Controller
          control={control}
          name="description"
          rules={getValidationRules('description')}
          render={({ field }) => (
            <Input
              {...field}
              autoCorrect={false}
              autoFocus
              accessibilityLabel="Ingredient name"
              hasError={!!errors.description}
              label="Name"
              style={styles.input}
              value={field.value || ''}
              onChangeText={field.onChange}
            />
          )}
        />
        <IngredientPortionForm
          showLabels
          serving={serving}
          servingOptions={SERVING_OPTIONS}
          quantity={quantity}
          onServingChange={setServing}
          onQuantityChange={setQuantity}
        />
        {serving === 'Other' && (
          <View style={styles.otherServingContainer}>
            <View style={styles.invisiblePlaceholder} />
            <Controller
              control={control}
              name="serving"
              rules={getValidationRules('serving')}
              render={({ field }) => (
                <Input
                  {...field}
                  autoCorrect={false}
                  accessibilityLabel="Serving name"
                  hasError={!!errors.serving}
                  placeholder="e.g. Piece"
                  style={styles.servingName}
                  value={field.value || ''}
                  onChangeText={field.onChange}
                />
              )}
            />
          </View>
        )}
        <Divider appearance="large" style={styles.divider} />
        <Text type="regular" bold style={styles.nutritionFactsHeading}>
          Nutrition Facts
        </Text>
        {macros.map(({ name, indent }) => (
          <View key={name}>
            <Controller
              control={control}
              name={name}
              rules={getValidationRules(name)}
              render={({ field }) => (
                <Input
                  {...field}
                  accessibilityLabel={name}
                  accessoryRight={<Text type="regular">{units[name]}</Text>}
                  hasError={!!errors[name]}
                  keyboardType="decimal-pad"
                  label={toWord(name)}
                  maxLength={16}
                  placeholder="0"
                  returnKeyType="done"
                  style={[styles.input, indent === 1 && styles.inputSecondary]}
                  value={field.value || ''}
                  onChangeText={(text) => {
                    const value = validateFloatNumberInput(text)
                    if (value) {
                      field.onChange(value)
                    }
                  }}
                />
              )}
            />
          </View>
        ))}
      </ScrollableAvoidKeyboard>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  content: {
    paddingTop: 24,
    paddingHorizontal: 16,
  },
  input: {
    zIndex: 1,
    marginBottom: 24,
  },
  inputSecondary: {
    marginLeft: 24,
  },
  backgroundInput: {
    position: 'absolute',
    width: '100%',
  },
  otherServingContainer: {
    flexDirection: 'row',
    marginTop: 16,
  },
  invisiblePlaceholder: {
    flex: 2,
    marginRight: 16,
  },
  servingName: {
    flex: 5,
  },
  divider: {
    marginVertical: 32,
    marginHorizontal: -16,
  },
  nutritionFactsHeading: {
    marginBottom: 32,
  },
})
